"use client";

import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { toast } from "@/lib/toast";
import { postWithAuth } from "@/lib/api-helpers";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { useAuth } from "@/hooks/use-auth";
import { useBodyScrollLock } from "@/components/tailwind/ui/anchored-popover";

interface InviteModalProps {
  onClose: () => void;
}

export default function InviteModal({ onClose }: InviteModalProps) {
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  // Inline validation message shown under the input, instead of a transient toast.
  const [error, setError] = useState<string | null>(null);
  const { currentWorkspace } = useWorkspaceContext();
  const { user } = useAuth();
  // Resolve the workspace's join policy — missing field on legacy docs falls
  // back to "org_only" (today's behavior, only same-domain invites allowed).
  const joinPolicy: "org_only" | "allowed_domains" | "any" =
    (currentWorkspace as any)?.joinPolicy === "any" ||
    (currentWorkspace as any)?.joinPolicy === "allowed_domains" ||
    (currentWorkspace as any)?.joinPolicy === "org_only"
      ? ((currentWorkspace as any).joinPolicy as "org_only" | "allowed_domains" | "any")
      : "org_only";
  const orgDomain = (
    currentWorkspace?.orgDomain?.trim() ||
    user?.email?.split("@")[1]?.trim() ||
    ""
  ).toLowerCase();
  const allowedDomains: string[] = Array.isArray((currentWorkspace as any)?.allowedDomains)
    ? ((currentWorkspace as any).allowedDomains as string[]).map((d) => d.toLowerCase())
    : [];
  // Resolve whether an email's domain is acceptable under the active policy.
  // Server enforces the same rule — this is just UX so users don't add chips
  // that will get rejected.
  const isDomainAllowed = (email: string): boolean => {
    const domain = email.split("@")[1]?.toLowerCase();
    if (!domain) return false;
    if (joinPolicy === "any") return true;
    if (orgDomain && domain === orgDomain) return true;
    if (joinPolicy === "allowed_domains" && allowedDomains.includes(domain)) return true;
    return false;
  };
  // Example domain shown in the placeholder/hint. For "any" we pick something
  // generic; otherwise show the workspace's own domain.
  const exampleDomain = joinPolicy === "any" ? "example.com" : orgDomain || "example.com";
  const modalRef = useRef<HTMLDivElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useBodyScrollLock(true);

  // 🔹 Focus input on open
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // 🔹 Keyboard shortcuts: ESC = close, Enter = submit
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [emails, inputValue, loading]);

  // 🔹 Prevent clicks inside modal from closing settings modal
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && modalRef.current.contains(e.target as Node)) {
        e.stopPropagation();
      }
    };
    document.addEventListener("mousedown", handleMouseDown, true);
    return () => document.removeEventListener("mousedown", handleMouseDown, true);
  }, []);

  // 🔹 Add email as chip when pressing Enter or comma
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const trimmed = inputValue.trim();
      if (!trimmed) return;

      const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
      if (!isValidEmail) {
        setError(`Enter a valid email (e.g. name@${exampleDomain})`);
        return;
      }
      if (!isDomainAllowed(trimmed)) {
        if (joinPolicy === "org_only") {
          setError(`Only @${orgDomain} emails can be added`);
        } else if (joinPolicy === "allowed_domains") {
          const list = [orgDomain, ...allowedDomains].filter(Boolean);
          setError(`Only emails from: ${list.map((d) => "@" + d).join(", ")} can be added`);
        } else {
          // "any" — should not hit, isDomainAllowed always returns true
          setError("This email is not allowed by the workspace policy");
        }
        return;
      }
      if (emails.includes(trimmed)) {
        setError("This email has already been added");
        return;
      }

      setEmails([...emails, trimmed]);
      setInputValue("");
      setError(null);
    }
  };

  const removeEmail = (email: string) => {
    setEmails(emails.filter((e) => e !== email));
  };

  const handleSendInvite = async () => {
    if (emails.length === 0) return toast.error("Add at least one valid email");

    try {
      setLoading(true);
      const response = await postWithAuth("/api/work-space/add-member", {
        membersEmail: emails,
        workspaceId: currentWorkspace?._id,
      });
      // New shape: { workspace, acceptedEmails, rejectedEmails }. Tolerate the
      // old shape (raw workspace) for in-flight clients.
      const rejected: string[] = Array.isArray((response as any)?.rejectedEmails)
        ? (response as any).rejectedEmails
        : [];
      const accepted: string[] = Array.isArray((response as any)?.acceptedEmails)
        ? (response as any).acceptedEmails
        : emails;
      if (accepted.length > 0) {
        toast.success(
          rejected.length > 0
            ? `Sent ${accepted.length} invite${accepted.length === 1 ? "" : "s"} — ${rejected.length} not allowed by workspace policy`
            : "Invites sent!",
        );
      }
      if (accepted.length === 0 && rejected.length > 0) {
        toast.error("None of those emails are allowed by the workspace policy");
      }
      onClose();
    } catch (err: any) {
      // Backend returns BadRequest with a structured rejectedEmails payload
      // when every email is filtered out by the policy.
      const data = err?.response?.data ?? err?.data;
      const rejected = Array.isArray(data?.rejectedEmails) ? data.rejectedEmails : null;
      toast.error(
        rejected
          ? `Rejected by policy: ${rejected.join(", ")}`
          : "Failed to send invites",
      );
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (typeof window === "undefined") return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking directly on the backdrop, not on the modal content
    if (e.target === backdropRef.current || e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Stop propagation to prevent settings modal from closing
    e.stopPropagation();
  };

  return createPortal(
    <div
      ref={backdropRef}
      // Bottom-anchored < lg (phones + iPads in portrait), centered at lg+.
      className="fixed inset-0 z-[10002] flex items-end lg:items-center justify-center bg-black/60 dark:bg-black/70 backdrop-blur-sm"
      onClick={handleBackdropClick}
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        ref={modalRef}
        // < lg: full width, top corners rounded only, max-h 90dvh,
        //   overflow-y-auto so the form scrolls under iOS keyboard.
        // lg+ (laptops/desktops): identical to today — 460px wide, fully rounded.
        className="bg-white dark:bg-[#191919] w-full lg:w-[460px] rounded-t-xl lg:rounded-xl flex flex-col gap-6 shadow-xl animate-in fade-in-0 zoom-in-95 overflow-hidden max-h-[90dvh] overflow-y-auto"
        style={{ boxShadow: "var(--c-shaOutScr, 0 8px 30px rgba(0,0,0,0.12))", paddingTop: "20px", paddingBottom: "20px" }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col items-center gap-2 px-6 relative">
          <div className="flex flex-col items-center gap-2 w-full">
            <svg aria-hidden="true" role="graphics-symbol" viewBox="0 0 20 20" className="w-9 h-9 text-gray-400 dark:text-[#9B9B9B] fill-current">
              <path d="M10 2.375c-1.137 0-2.054.47-2.674 1.242-.608.757-.9 1.765-.9 2.824s.292 2.066.9 2.824c.62.772 1.537 1.241 2.674 1.241s2.055-.469 2.675-1.241c.608-.758.9-1.766.9-2.824 0-1.059-.292-2.067-.9-2.824-.62-.773-1.538-1.242-2.675-1.242M7.676 6.441c0-.842.233-1.554.624-2.042.38-.473.937-.774 1.7-.774s1.32.301 1.7.774c.391.488.624 1.2.624 2.042s-.233 1.554-.624 2.041c-.38.473-.937.774-1.7.774s-1.32-.3-1.7-.774c-.391-.487-.624-1.2-.624-2.041M10 11.63c-2.7 0-5.101 1.315-6.12 3.305-.361.706-.199 1.421.23 1.923.412.48 1.06.767 1.74.767h5.845a4.6 4.6 0 0 1-.28-1.25H5.85c-.342 0-.634-.148-.79-.33-.138-.162-.172-.336-.067-.54.761-1.488 2.672-2.626 5.007-2.626.83 0 1.605.144 2.294.395.269-.364.59-.688.952-.96A7.9 7.9 0 0 0 10 11.63"></path>
              <path d="M16 19.5a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7m2.594-3.469a.625.625 0 0 0-.625-.625h-1.375v-1.375a.625.625 0 0 0-1.25 0v1.375h-1.375a.625.625 0 0 0 0 1.25h1.375v1.375a.625.625 0 1 0 1.25 0v-1.375h1.375c.345 0 .625-.28.625-.625"></path>
            </svg>
            <div className="text-[17px] leading-[22px] font-semibold text-center text-gray-900 dark:text-gray-200" style={{ textWrap: "balance" }}>
              Add members
            </div>
          </div>
          <div className="text-[14px] leading-[20px] font-normal text-center text-gray-500 dark:text-[#9B9B9B] w-full truncate" style={{ textWrap: "balance" }}>
            Add one or more email addresses. Use commas or press Enter to separate multiple addresses.
          </div>
        </div>

        {/* Email chips + input */}
        <div className="px-6">
          <div className="w-full flex items-start flex-wrap bg-gray-50 dark:bg-[rgb(42,42,42)] cursor-text overflow-hidden min-h-[28px] px-2 py-1.5 rounded-md" style={{ boxShadow: error ? "rgba(239, 68, 68, 0.6) 0px 0px 0px 1px inset" : "rgba(15, 15, 15, 0.1) 0px 0px 0px 1px inset" }}>
            <div className="flex flex-wrap flex-grow gap-1 min-w-0">
              {emails.map((email) => (
                <span
                  key={email}
                  className="flex items-center gap-1 bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-500 px-2 rounded-sm text-[13px] font-medium leading-[20px]"
                >
                  {email}
                  <X
                    className="w-3 h-3 cursor-pointer hover:text-red-500 opacity-70 hover:opacity-100 transition-opacity"
                    onClick={() => removeEmail(email)}
                  />
                </span>
              ))}

              <div className="flex-1 min-w-[60px] leading-[20px] rounded-sm">
                <input
                  ref={inputRef}
                  type="email"
                  value={inputValue}
                  onChange={(e) => {
                    setInputValue(e.target.value);
                    if (error) setError(null);
                  }}
                  onKeyDown={handleKeyPress}
                  placeholder={emails.length ? "" : "Search names or emails"}
                  className="w-full h-[20px] bg-transparent outline-none text-[13px] text-gray-900 dark:text-gray-100 placeholder-faint"
                />
              </div>
            </div>
          </div>
          {error && (
            <p className="mt-1.5 text-[13px] text-red-500 dark:text-red-400">{error}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 px-6">
          <button
            onClick={handleSendInvite}
            disabled={emails.length === 0 || loading}
            className="flex items-center justify-center h-8 px-3 text-[14px] font-medium text-white bg-blue-500 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Sending..." : "Send invite"}
          </button>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-8 px-3 text-[14px] font-medium text-gray-600 dark:text-gray-400 hover-component  rounded-md transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
